import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { MouseEvent, ReactNode } from 'react';

import { archivedBackgroundUrl } from '@adflow/frontend/ui-assets';
import { ButtonIconAdd } from '@adflow/frontend/ui-buttons';
import { HubErrorBoundary } from '@adflow/frontend/ui-common';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { theme, typography } from '@adflow/frontend/ui-theme';
import type { NavAction } from '@adflow/frontend/util-router';
import {
	LinkIconAdd,
	LinkNavAction,
	useTableParams,
} from '@adflow/frontend/util-router';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';

import { SidebarPagination } from './SidebarPagination';

const styles = {
	container: css({
		display: 'grid',
		height: '100%',
		gridTemplateColumns: '16rem calc(100% - 16rem)',
		gridTemplateRows: '100%',
	}),
	aside: css({
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'auto',
		background: `linear-gradient(to right, ${theme.colors.background.default} 96%, ${theme.colors.gray[200]})`,
	}),
	titleContainer: css({
		backgroundColor: theme.colors.transparent,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: '4rem',
		paddingLeft: '1rem',
		paddingRight: '1.25rem',
		paddingTop: '0.75rem',
		paddingBottom: '0.75rem',
	}),
	title: css(typography.title, {
		// Indicate when the title is a link.
		'a&': {
			color: theme.colors.text.tableHeader,
			cursor: 'pointer',
		},
	}),
	listItem: css(
		{
			position: 'relative',
			display: 'flex',
			alignItems: 'center',
			paddingLeft: '1rem',
			paddingRight: '1rem',
			paddingTop: '0.75rem',
			paddingBottom: '0.75rem',
			cursor: 'pointer',
			height: '3.5rem',
		},
		typography.regular
	),

	listItemArchived: css({
		color: theme.colors.text.archivedRow,
		fontStyle: 'italic',
	}),
	listItemSelected: css({
		background: theme.colors.background.form,
		boxShadow: '0px 0px 7px 6px #00000017',
		clipPath: 'inset(-3rem 0 -3rem 0)',
	}),
	listItemSelectedArchived: css({
		background: '#EEE',
	}),
	listItemReadonly: css({
		color: theme.colors.text.note,
		fontWeight: 700,
		flexDirection: 'column',
		alignItems: 'normal',
		justifyContent: 'flex-start',
	}),
	listItemSelectedReadonly: css({
		background: theme.colors.background.form,
		boxShadow: '0px 0px 7px 6px #00000017',
		clipPath: 'inset(-3rem 0 -3rem 0)',
		color: theme.colors.text.note,
		fontWeight: 700,
		flexDirection: 'column',
		alignItems: 'normal',
		justifyContent: 'flex-start',
	}),
	main: css({ height: '100%', backgroundColor: theme.colors.background.form }),
	mainArchived: css({
		backgroundColor: '#EEE',
		backgroundImage: `url('${archivedBackgroundUrl}')`,
		backgroundRepeat: 'no-repeat',
		backgroundSize: 'contain',
		backgroundPosition: 'center',
	}),
};

export interface SidebarViewProps {
	title: string;
	itemType: DataTableItemType;
	/** If this is undefined the add button will not be rendered. */
	onClickAddButton?: (e: MouseEvent<HTMLElement>) => void;
	addButtonNavAction?: NavAction;
	addButtonDisabled?: boolean;
	listItemNavAction: (index: number) => NavAction;
	titleNavAction?: NavAction;
	list: {
		key: string;
		value?: string;
		archived?: boolean;
		readonly?: boolean;
	}[];
	readonlyText?: ReactNode;
	selectedListItemIndex: number;
	subheader?: ReactNode;
	filterButton?: ReactNode;
	content?: ReactNode;
	itemRenderer?: (key: string, value?: string) => ReactNode;
	listStyle?: CSSInterpolation;
	showPagination?: boolean;
	totalRows?: number;
	isSidebarLoading?: boolean;
	selectedArchived?: boolean;

	/** For overriding the root element's styles. */
	className?: string;
}

export function SidebarView({
	title,
	itemType,
	onClickAddButton,
	addButtonNavAction,
	listItemNavAction,
	titleNavAction,
	addButtonDisabled,
	list,
	readonlyText,
	selectedListItemIndex,
	subheader,
	filterButton,
	content,
	itemRenderer,
	listStyle,
	className,
	showPagination,
	totalRows,
	isSidebarLoading,
	selectedArchived,
}: SidebarViewProps) {
	const { currentPage, rowsPerPage, setCurrentPage, tableWrapRef } =
		useTableParams({ itemType });

	return (
		<div css={styles.container} className={className}>
			<aside css={styles.aside}>
				<div css={styles.titleContainer}>
					<div
						css={{
							display: 'flex',
							alignItems: 'center',
						}}
					>
						{titleNavAction === undefined ? (
							<p css={styles.title}>{title}</p>
						) : (
							<LinkNavAction css={styles.title} to={titleNavAction}>
								{title}
							</LinkNavAction>
						)}
						{filterButton ? (
							<div
								css={{
									padding: '0.5rem',
								}}
							>
								{filterButton}
							</div>
						) : null}
					</div>
					{addButtonNavAction ? (
						<LinkIconAdd
							to={addButtonNavAction}
							onClick={onClickAddButton}
							data-testid="sidebar-add-button"
							data-disabled={addButtonDisabled}
						/>
					) : onClickAddButton ? (
						<ButtonIconAdd
							onClick={onClickAddButton}
							data-testid="sidebar-add-button"
							data-disabled={addButtonDisabled}
						/>
					) : null}
				</div>
				{subheader}
				<ul ref={tableWrapRef} css={[{ flexGrow: '1' }, listStyle]}>
					{list.length === 0 ? null : isSidebarLoading ? (
						<LoadingIndicator />
					) : (
						list.map(
							({ key, value, archived = false, readonly = false }, index) => {
								const selected = index === selectedListItemIndex;

								return (
									<li key={key || index}>
										<LinkNavAction
											css={[
												styles.listItem,
												readonly ? styles.listItemReadonly : null,
												archived ? styles.listItemArchived : null,
												selected ? styles.listItemSelected : null,
												selected && archived
													? styles.listItemSelectedArchived
													: null,
												selected && readonly
													? styles.listItemSelectedReadonly
													: null,
											]}
											to={listItemNavAction(index)}
											data-testid="sidebar-list-item"
										>
											{itemRenderer ? itemRenderer(key, value) : (value ?? key)}
											{readonly ? readonlyText : null}
										</LinkNavAction>
									</li>
								);
							}
						)
					)}
				</ul>

				{showPagination ? (
					<SidebarPagination
						rowsPerPage={rowsPerPage ?? 0}
						totalItems={totalRows ?? 0}
						page={currentPage}
						onChangePage={setCurrentPage}
						isLoading={totalRows === undefined || totalRows === Infinity}
					/>
				) : null}
			</aside>
			<main css={[styles.main, selectedArchived ? styles.mainArchived : null]}>
				<HubErrorBoundary>{content}</HubErrorBoundary>
			</main>
		</div>
	);
}
